import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Avatar, Modal, Pagination, theme, Typography } from 'antd';
import { DataTable } from '../../../components/DataTable';
import { EmptyState } from '../../../components/EmptyState';
import { KpiRow } from '../../dashboard/components/KpiRow';
import { KpiCard } from '../../dashboard/components/KpiCard';
import { useCatalogos } from '../../catalogos/api/useCatalogos';
import { clientesKeys } from '../../clientes/api/keys';
import { AnularPagoModal } from './AnularPagoModal';
import { PagoCreateModal } from './PagoCreateModal';
import { PagoDetailModal } from './PagoDetailModal';
import { BoletaViewer } from './BoletaViewer';
import { PagosTableToolbar } from './PagosTableToolbar';
import { createPagosTableColumns } from './PagosTableColumns';
import { usePagosList, usePagosStats, usePagosUsers } from '../api/usePagos';
import { useHasPermission } from '../../../auth/useHasPermission';
import { paginationProps } from '../../../lib/paginacion';
import { useServerPaginacion } from '../../../lib/server-sort';
import { useT } from '../../../locales';
import { formatCurrency, Permission } from '@aid/shared';
import type { PagoClienteResponse } from '@aid/shared';
import {
  groupPagos,
  getPagoInitials,
  buildPagosQueryFilters,
  isClientOutsideCobradorWorklistError,
} from '../lib/pago-table-helpers';
import {
  EMPTY_PAGO_TABLE_FILTERS,
  PAGOS_TABLE_GROUP,
  PAGOS_TABLE_SCOPE,
  getPagosTableCapabilities,
} from './pago-table-types';
import type { PagosTableProps, PagoTableFilterState } from './pago-table-types';
import type { PagosTableGroupBy } from './pago-table-types';

const { Text } = Typography;

export { PAGOS_TABLE_SCOPE } from './pago-table-types';
export type { PagosTableProps } from './pago-table-types';

export function PagosTable(props: PagosTableProps) {
  const t = useT();
  const { token } = theme.useToken();
  const queryClient = useQueryClient();
  const { scope } = props;
  const capabilities = getPagosTableCapabilities(scope);
  const fixedClienteId = scope === PAGOS_TABLE_SCOPE.CLIENTE ? props.clienteId : undefined;
  const { page, pageSize, sort, handleChange, setPage } = useServerPaginacion<PagoClienteResponse>(
    capabilities.initialPageSize,
  );
  const [filters, setFilters] = useState<PagoTableFilterState>(EMPTY_PAGO_TABLE_FILTERS);
  const [groupBy, setGroupBy] = useState<PagosTableGroupBy>(PAGOS_TABLE_GROUP.NONE);
  const [anularTarget, setAnularTarget] = useState<PagoClienteResponse | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [detailId, setDetailId] = useState<number | null>(null);
  const [boletaPagoId, setBoletaPagoId] = useState<number | null>(null);

  const queryFilters = buildPagosQueryFilters(scope, filters, fixedClienteId);
  const { data, isLoading, error, refetch } = usePagosList({
    limit: pageSize,
    offset: (page - 1) * pageSize,
    ...queryFilters,
    ...sort,
  });

  const { data: stats, isLoading: statsLoading } = usePagosStats(queryFilters, {
    enabled: capabilities.showStats,
  });
  const { data: estadosPago } = useCatalogos('ESTADO_PAGO');
  const { data: tiposPago } = useCatalogos('TIPO_PAGO');
  const canCreate = useHasPermission(Permission.PAGOS_CREATE);
  const canAnular = useHasPermission(Permission.PAGOS_ANULAR);

  // User filter options: distinct users associated with payments, loaded only
  // in the scopes that show the control. The toolbar searches client-side.
  const { data: pagosUsersData } = usePagosUsers({ enabled: capabilities.showUserFilter });
  const userOptions = pagosUsersData ?? [];

  const rows = data?.data ?? [];
  const hasFilters =
    (capabilities.showClientFilter && filters.idCliente.trim() !== '') ||
    filters.idEstadoPago !== undefined ||
    filters.idTipoPago !== undefined ||
    (capabilities.showUserFilter && filters.idUsuario !== undefined) ||
    filters.fechaDesde !== undefined ||
    filters.fechaHasta !== undefined;
  const groups = groupPagos(rows, groupBy, t);
  const caption = hasFilters ? t('common.segunFiltros') : undefined;
  const statsEmpty = statsLoading && !stats;
  const isClientOutsideWorklist = isClientOutsideCobradorWorklistError(error);
  const emptyState = (
    <EmptyState
      compact={capabilities.compactEmptyState}
      title={
        capabilities.compactEmptyState
          ? t('pages.clientes.actividad.sinPagos')
          : t('pages.pagos.emptyTitle')
      }
      description={
        capabilities.compactEmptyState
          ? t('pages.clientes.actividad.sinPagosDesc')
          : t('pages.pagos.emptyDescription')
      }
    />
  );

  const updateFilters = (next: Partial<PagoTableFilterState>) => {
    setFilters((current) => ({ ...current, ...next }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters({ ...EMPTY_PAGO_TABLE_FILTERS });
    setPage(1);
  };

  const handleUserChange = (value: string | undefined) => {
    updateFilters({ idUsuario: value });
  };

  const handlePaymentChanged = () => {
    void refetch();
    if (fixedClienteId === undefined) return;
    void queryClient.invalidateQueries({ queryKey: clientesKeys.detail(fixedClienteId) });
    void queryClient.invalidateQueries({ queryKey: clientesKeys.cobros(fixedClienteId) });
    void queryClient.invalidateQueries({ queryKey: clientesKeys.serieMensualBase(fixedClienteId) });
  };

  const columns = createPagosTableColumns({
    t,
    showClient: capabilities.showClientColumn,
    showUser: capabilities.showUserFilter,
    groupBy,
    canAnular,
    onDetail: setDetailId,
    onAnular: setAnularTarget,
    onViewBoleta: setBoletaPagoId,
    primaryColor: token.colorPrimary,
    sort,
  });

  const toolbar = (
    <PagosTableToolbar
      t={t}
      showClientFilter={capabilities.showClientFilter}
      showUserFilter={capabilities.showUserFilter}
      showGrouping={capabilities.showGrouping}
      groupBy={groupBy}
      idCliente={filters.idCliente}
      idEstadoPago={filters.idEstadoPago}
      idTipoPago={filters.idTipoPago}
      idUsuario={filters.idUsuario}
      userOptions={userOptions}
      fechaDesde={filters.fechaDesde}
      fechaHasta={filters.fechaHasta}
      estadosPago={estadosPago ?? []}
      tiposPago={tiposPago ?? []}
      canCreate={canCreate}
      hasFilters={hasFilters}
      onGroupByChange={(value) => {
        setGroupBy(value);
        setPage(1);
      }}
      onClientChange={(value) => updateFilters({ idCliente: value })}
      onEstadoChange={(value) => updateFilters({ idEstadoPago: value })}
      onTipoChange={(value) => updateFilters({ idTipoPago: value })}
      onUserChange={handleUserChange}
      onDateRangeChange={(value) =>
        updateFilters({
          fechaDesde: value?.[0] ?? undefined,
          fechaHasta: value?.[1] ?? undefined,
        })
      }
      onClear={clearFilters}
      onCreate={() => setCreateOpen(true)}
    />
  );

  return (
    <div>
      {capabilities.showStats && (
        <div style={{ marginBottom: 16 }}>
          <KpiRow>
            <KpiCard
              label={t('pages.pagos.kpis.recaudado')}
              value={stats?.recaudado ?? '0'}
              accent="pagos"
              caption={caption}
              empty={statsEmpty}
            />
            <KpiCard
              label={t('pages.pagos.kpis.vigentes')}
              value={String(stats?.vigentes ?? 0)}
              formatValue={false}
              accent="clientes"
              caption={caption}
              empty={statsEmpty}
            />
            <KpiCard
              label={t('pages.pagos.kpis.anuladas')}
              value={String(stats?.anuladas ?? 0)}
              formatValue={false}
              accent="mora"
              caption={caption}
              empty={statsEmpty}
            />
            <KpiCard
              label={t('pages.pagos.kpis.ticketPromedio')}
              value={stats?.promedio ?? '0'}
              accent="none"
              caption={caption}
              empty={statsEmpty}
            />
          </KpiRow>
        </div>
      )}
      {toolbar}
      {error ? (
        <EmptyState
          compact={capabilities.compactEmptyState}
          title={
            isClientOutsideWorklist
              ? t('pages.pagos.clientOutsideWorklist')
              : t('pages.pagos.errorLoad')
          }
          description={
            isClientOutsideWorklist ? t('pages.pagos.clientOutsideWorklistDescription') : undefined
          }
        />
      ) : groups ? (
        groups.length === 0 ? (
          <DataTable<PagoClienteResponse>
            columns={columns}
            dataSource={rows}
            rowKey="id"
            loading={isLoading}
            locale={{
              emptyText: emptyState,
            }}
            pagination={false}
            onChange={handleChange}
          />
        ) : (
          <>
            {groups.map((group) => (
              <div key={group.label} style={{ marginBottom: 24 }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 12px',
                    marginBottom: 8,
                    borderRadius: 8,
                    background: token.colorFillTertiary,
                    border: `1px solid ${token.colorBorderSecondary}`,
                  }}
                >
                  {groupBy === PAGOS_TABLE_GROUP.CLIENTE && (
                    <Avatar
                      size={24}
                      style={{ background: token.colorPrimary, fontSize: 12 }}
                      aria-hidden
                    >
                      {getPagoInitials(group.rows[0]?.cliente?.nombre)}
                    </Avatar>
                  )}
                  <Text strong>{group.label}</Text>
                  <Text type="secondary">{group.rows.length}</Text>
                  <Text type="secondary" style={{ marginLeft: 'auto' }}>
                    {formatCurrency(
                      group.rows.reduce((total, pago) => total + Number(pago.valor), 0).toFixed(2),
                    )}
                  </Text>
                </div>
                <DataTable<PagoClienteResponse>
                  columns={columns}
                  dataSource={group.rows}
                  rowKey="id"
                  pagination={false}
                  onChange={handleChange}
                />
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
              <Pagination
                current={page}
                pageSize={pageSize}
                total={data?.total ?? 0}
                showSizeChanger={false}
                showTotal={(total, range) =>
                  t('pagination.showTotal', { from: range[0], to: range[1], total })
                }
                onChange={setPage}
              />
            </div>
          </>
        )
      ) : (
        <DataTable<PagoClienteResponse>
          columns={columns}
          dataSource={data?.data}
          rowKey="id"
          loading={isLoading}
          locale={{
            emptyText: emptyState,
          }}
          pagination={paginationProps(t, {
            page,
            pageSize,
            total: data?.total ?? 0,
            showSizeChanger: capabilities.showPaginationSizeChanger,
          })}
          onChange={handleChange}
        />
      )}
      <AnularPagoModal
        open={anularTarget !== null}
        pagoId={anularTarget?.id ?? 0}
        onClose={() => setAnularTarget(null)}
        onSuccess={handlePaymentChanged}
      />
      <PagoCreateModal
        open={createOpen}
        preselectClienteId={fixedClienteId}
        onClose={() => setCreateOpen(false)}
        onSuccess={handlePaymentChanged}
      />
      {detailId !== null && (
        <PagoDetailModal
          id={detailId}
          onClose={() => setDetailId(null)}
          onSuccess={handlePaymentChanged}
        />
      )}
      <Modal
        title={t('pages.pagos.boletaViewerTitle')}
        open={boletaPagoId !== null}
        onCancel={() => setBoletaPagoId(null)}
        footer={null}
        centered
        destroyOnHidden
        width="min(720px, calc(100vw - 24px))"
      >
        {boletaPagoId !== null ? <BoletaViewer id={boletaPagoId} /> : null}
      </Modal>
    </div>
  );
}
